/**
 * Illustrations for theory lessons.
 * Generated by local-resolver/scripts/generate_theory_examples.py
 *
 * - kind: 'image' for musician/history portraits
 * - kind: 'notation' for concepts shown with abcjs (notes, not chord symbols alone)
 */

import celticExamples from './examples-celtic.js'

const WIKI = "https://upload.wikimedia.org/wikipedia/commons"

/** Verified Commons URLs (broken hash paths were causing missing feed images). */
const IMAGES = {
  armstrong: WIKI + "/1/16/Louis_Armstrong_in_Color_%28restored%29.jpg",
  debussy: WIKI + "/c/c3/Claude_Debussy_by_Atelier_Nadar.jpg",
  ellington: WIKI + "/8/85/Richard_Nixon_and_Duke_Ellington_1969.jpg",
  parker: WIKI + "/8/82/Portrait_of_Charlie_Parker_in_1947.jpg",
  lomax: WIKI + "/c/c0/Alan_Lomax.jpg",
  ritchie: WIKI + "/7/70/Jean_Ritchie_-_Appalachian_folk_singer%2C_1950.jpg",
  harpsichord: WIKI + "/1/1c/Clavecin_Blanchet_-_Versailles-01.jpg",
  irishDancing: WIKI + "/8/82/Lace_on_Irish_stepdance_dress.JPG",
  moog: WIKI + "/2/28/1973_Moog_modular_synthesizer.jpg",
  dufay: WIKI + "/4/4f/DufayBinchois.jpg",
  turntable: WIKI + "/6/6e/Top_view_of_a_turntable_%28Unsplash%29.jpg",
}

function notation(illustrationPlan, abc, metadata) {
  return {
    kind: 'notation',
    illustrationPlan: illustrationPlan,
    abc: abc,
    full: true,
    metadata: Object.assign({ meter: '4/4', noteLength: '1/8', key: 'C' }, metadata || {}),
  }
}

function portrait(illustrationPlan, imageUrl) {
  return {
    kind: 'image',
    illustrationPlan: illustrationPlan,
    imageUrl: imageUrl,
    abc: '',
  }
}

function noExample() {
  return {
    kind: 'none',
    illustrationPlan: '',
    imageUrl: '',
    abc: '',
  }
}

export const THEORY_LESSON_EXAMPLES = {
  "chords-diatonic-major-01": notation(
    "G major diatonic triads ascending by scale degree: each measure shows a stacked root-position triad with Roman numeral and chord quality (maj/min/dim) above and the chord letter name in parentheses.",
    [
      "V:1 clef=treble",
      "\"I maj (G)\" [GBd] |",
      "\"ii min (Am)\" [Ace] |",
      "\"iii min (Bm)\" [Bdf] |",
      "\"IV maj (C)\" [CEG] |",
      "\"V maj (D)\" [Df^a] |",
      "\"vi min (Em)\" [EGB] |",
      "\"vii° dim (F#)\" [^FAc] |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "G" }
  ),
  "chords-diatonic-minor-01": notation(
    "A natural minor diatonic triads ascending by scale degree: each measure shows a stacked root-position triad with Roman numeral, chord quality, and letter name.",
    [
      "V:1 clef=treble",
      "\"i min (Am)\" [ACE] |",
      "\"ii° dim (B)\" [BDF] |",
      "\"III maj (C)\" [CEG] |",
      "\"iv min (Dm)\" [DFA] |",
      "\"v min (Em)\" [EGB] |",
      "\"VI maj (F)\" [FAC] |",
      "\"VII maj (G)\" [GBd] |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "Am" }
  ),
  "chords-extensions-01": notation(
    "C major triad then C9, C11, and C13 as stacked block chords showing how each extension adds a higher tone.",
    [
      "V:1 clef=treble",
      "\"C\" [CEG] |",
      "\"C9\" [CEGBd] |",
      "\"C11\" [CEGBdfa] |",
      "\"C13\" [CEGBdfac] |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
  "chords-inversions-01": notation(
    "Single treble staff: C-major triad in root position, first inversion, second inversion, then root position one octave higher.",
    [
      "V:1 clef=treble",
      "I(C) [CEG] | I⁶(C/E) [EGc] | I⁶⁴(C/G) [Gce] | I(C) [ceg] |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
  "chords-jazz-color-01": notation(
    "Jazz colour: V7b9(G) [GBDF_A] resolves to I(Cmaj7) [CEGB]; tritone sub bII7(Db) [_DBF_A] resolves to the same I(Cmaj7) [CEGB].",
    [
      "V:1 clef=treble",
      "\"V7b9(G)\" [GBDF_A] | \"I(Cmaj7)\" [CEGB] | \"bII7(Db)\" [_DBF_A] | \"I(Cmaj7)\" [CEGB] |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
  "chords-sevenths-01": notation(
    "Compare Cmaj7(C) [CEGB] and G7(G) [GBDF] as stacked seventh chords, with the 3rd and 7th forming the guide-tone pair in each.",
    [
      "V:1 clef=treble",
      "\"Cmaj7(C)\" [CEGB] | \"G7(G)\" [GBDF] |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
  "chords-triads-01": notation(
    "Display four triads on the staff using stacked ABC chord symbols: [CEG] (C major), [ACE] (A minor), [BDF] (B diminished), and [CEG#] (C augmented).",
    [
      "V:1 clef=treble",
      "| I(C) [CEG] | vi(Am) [ACE] | vii°(Bdim) [BDF] | I+(C) [CE^G] |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "C" }
  ),
  "foundations-accidentals-01": notation(
    "Display a two‑measure treble‑clef excerpt: the first measure in G major showing the F♯ from the key signature on an F note, and the second measure in C major where a natural sign cancels that F♯ on an F, plus a B♭ accidental on a B note—all within the staff without ledger lines.",
    [
      "V:1 clef=treble",
      "[K:G] D F G A | [K:C] =F _B G A |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "G" }
  ),
  "foundations-pitch-01": notation(
    "Display a treble clef with a short sequence of notes that lands on every line (E, G, B, D, F) and every space (F, A, C, E) of the five‑line staff, using only notes that stay within the staff bounds.",
    [
      "V:1 clef=treble",
      "[K:C] E F G A | B C D E | F2 |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
  "foundations-rhythm-01": notation(
    "Display a 4/4 bar with a whole note, two half notes, four quarter notes, and eight eighth notes, followed by a 6/8 bar showing two dotted‑quarter notes and six eighth notes, all written in ABC notation using only staff‑center notes (no ledger lines).",
    [
      "V:1 clef=treble",
      "[M:4/4][L:1/8] a8 | a4 a4 | a2 a2 a2 a2 | a a a a a a a a | [M:6/8] a3 a3 | a a a a a a |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "C" }
  ),
  "harmony-cadences-01": notation(
    "Perfect authentic cadence V→I and plagal cadence IV→I as stacked block chords in C major.",
    [
      "V:1 clef=treble",
      "\"V(G)\" [GBD] | \"I(C)\" [CEG] |",
      "\"IV(F)\" [FAC] | \"I(C)\" [CEG] |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
  "harmony-counterpoint-01": notation(
    "Two voices in contrary motion: as the soprano ascends d–e–f–g, the alto descends b–a–g–f on the staff.",
    [
      "V:1 clef=treble",
      "d e f g |",
      "V:2 clef=treble",
      "b a g f |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "D" }
  ),
  "harmony-modulation-01": notation(
    "Show a G‑major → C‑major pivot‑chord modulation using stacked ABC chords [GBD] → [CEG] with Roman numerals I(G) – IV(C) – I(C).",
    [
      "V:1 clef=treble",
      "[K:G] I(G) [GBD] | IV(C) [CEG] | [K:C] I(C) [CEG] |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "G" }
  ),
  "harmony-phrases-01": notation(
    "Two-line lyrical phrase: slur over each four-bar antecedent and consequent phrase with lyrics underneath.",
    [
      "V:1 clef=treble",
      "(d e f g) (a b c d) |",
      "w: In Down- by the |",
      "w: road where I |",
      "(e f g a) (f e d A) |",
      "w: met her first |",
      "w: she smiled at me |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "D" }
  ),
  "harmony-secondary-01": notation(
    "Show the secondary dominant A7 [A^C E G] resolving to D minor [D F A] with Roman numerals V7/vi → vi in the key of C major.",
    [
      "V:1 clef=treble",
      "V7/vi(A7) [A^C E G] | vi(Dm) [D F A] |",
      "V:2 clef=bass",
      "V7/vi(A7) A | vi(Dm) D |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "C" }
  ),
  "harmony-tendency-01": notation(
    "Simple dominant seventh V7(G) resolving to tonic I(C) as two stacked block chords.",
    [
      "V:1 clef=treble",
      "\"V7(G)\" [GBDF]2 | \"I(C)\" [CEG]2 |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/2", key: "C" }
  ),
  "harmony-voiceleading-01": notation(
    "Four-part I(C)–vi(Am)–IV(F)–I(C) voice leading: each voice has bar lines and quarter-note motion with chord tones on every beat.",
    [
      "V:1 clef=treble",
      "e e f e |",
      "V:2 clef=treble",
      "e c a c |",
      "V:3 clef=treble",
      "g a f g |",
      "V:4 clef=bass",
      "C A F C |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
  "history-armstrong-01": portrait(
    "Portrait of Louis Armstrong playing trumpet — representative image for his swing phrasing and improvisation legacy.",
    IMAGES.armstrong
  ),
  "history-bach-01": portrait(
    "Create a stylized portrait of J.S. Bach with surrounding icons representing a two‑part invention, a gigue rhythm, and a keyboard manuscript of the Well‑Tempered Clavier.",
    WIKI + "/6/6a/Johann_Sebastian_Bach.jpg"
  ),
  "history-beethoven-01": portrait(
    "Display a portrait of Beethoven alongside the famous four‑note motive (G‑G‑G‑E♭) with its short‑short‑short‑long rhythmic pattern and a crescendo dynamic marking.",
    WIKI + "/6/6f/Beethoven.jpg"
  ),
  "history-chopin-01": portrait(
    "Show a portrait of Chopin at the piano highlighting the right‑hand singing melody line above the left‑hand harmonic accompaniment, and annotate the Polish mazurka rhythm (triple meter with accent shifts) and a rubato timing curve.",
    WIKI + "/e/e8/Frederic_Chopin_photo.jpeg"
  ),
  "history-debussy-01": portrait(
    "Portrait of Claude Debussy — representative image for his colour, modes, and new sonorities.",
    IMAGES.debussy
  ),
  "history-ellington-01": portrait(
    "Duke Ellington with his big band era represented — colour and orchestration in jazz composition.",
    IMAGES.ellington
  ),
  "history-mozart-01": portrait(
    "Show a portrait of Mozart beside a four‑measure staff excerpt illustrating an antecedent‑consequent phrase with I–V–I harmony and an Alberti bass pattern.",
    WIKI + "/1/1e/Wolfgang-amadeus-mozart_1.jpg"
  ),
  "history-parker-01": portrait(
    "Portrait of Charlie Parker with alto saxophone — representative image for bebop and harmonic velocity.",
    IMAGES.parker
  ),
  "history-trad-collectors-01": portrait(
    "Alan Lomax in the field — representative portrait for Francis O’Neill, Cecil Sharp, Alan Lomax, and other tradition collectors.",
    IMAGES.lomax
  ),
  "history-women-01": portrait(
    "Jean Ritchie, Appalachian folk singer — representative image for women composers and tradition bearers in music history.",
    IMAGES.ritchie
  ),
  "intervals-01": notation(
    "Display a short melodic line on the treble staff—G4, A4, B4, C5, D5—to illustrate a major second (G–A), a major third (G–B), a perfect fourth (G–C), and a perfect fifth (G–D), and show the harmonic interval G4–D5 stacked vertically.",
    [
      "V:1 clef=treble",
      "G A B c d |]",
      "V:2 clef=treble",
      "[Gd] z4 |]",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "C" }
  ),
  "italian-articulation-01": notation(
    "Display a single 4/4 measure with four quarter‑note pitches D‑E‑F‑G, using a slur over the first two notes to show legato, a staccato dot on the third note, and an accent (>) on the fourth note.",
    [
      "V:1 clef=treble",
      "| (D E) !staccato!F >G |",
    ].join('\n'),
    { meter: "4/4", noteLength: "quarter", key: "C" }
  ),
  "italian-dynamics-01": notation(
    "A four‑note phrase on the staff (C‑D‑E‑F) showing a piano (p) marking on the first note, mezzo‑forte (mf) on the second, a crescendo hairpin leading to forte (f) on the third, and a sforzando (sfz) accent on the final note.",
    [
      "V:1 clef=treble",
      "|!p!C !mf!D< !f!E !sfz!F|",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
  "italian-form-01": notation(
    "Repeat signs (|: :|), first ending [1, and D.C. al fine marking on a short melody — all on the staff without ledger lines.",
    [
      "V:1 clef=treble",
      "|: d e f g :| [1 g f e d :| [2 a g f e |] d4 |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "D" }
  ),
  "italian-tempo-01": notation(
    "Display a single 4/4 staff with four quarter‑note beats (C D E F) and place the tempo markings Allegro, Andante, and Presto above the staff as they would appear in a printed score.",
    [
      "%%text Allegro",
      "V:1 clef=treble",
      "c D E F |",
      "%%text Andante",
      "c D E F |",
      "%%text Presto",
      "c D E F |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
  "keys-circle-01": portrait(
    "Colorful circle-of-fifths diagram with major keys on the outer ring, relative minors inside, and treble-clef key signatures around the perimeter; clockwise steps add sharps from C, counter-clockwise steps add flats.",
    "/feed_images/circle-of-fifths.png"
  ),
  "scales-major-01": notation(
    "Show a C‑major scale (C D E F G A B C) and a G‑major scale (G A B C D E F♯ G) on a treble‑clef staff, labeling each degree (1‑7‑8) and highlighting the leading tone (B).",
    [
      "V:1 clef=treble",
      "[K:C] C D E F G A !LT!B c |",
      "w: 1 2 3 4 5 6 7 8",
      "[K:G] G A B c d e !LT!f g |",
      "w: 1 2 3 4 5 6 7 8",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "C" }
  ),
  "scales-minor-01": notation(
    "Display the A natural, harmonic, and melodic minor scales in ABC notation on a single staff, highlighting the raised seventh (G♯) in harmonic minor and the raised sixth (F♯) and seventh (G♯) in melodic minor.",
    [
      "V:1 clef=treble",
      "[K:Am] A B C D E F G A | [K:Am] A B C D E F ^G A | [K:Am] A B C D E ^F ^G A |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "Am" }
  ),
  "styles-baroque-01": portrait(
    "Baroque harpsichord (clavecin) — representative image for ornament, continuo, and dance-suite style.",
    IMAGES.harpsichord
  ),
  "styles-blues-01": notation(
    "Twelve-bar blues in C as three lines of block chords with Roman numerals: bars 1–4 I, 5–8 IV–IV–I–I, 9–12 V–IV–I–I; each chord lasts two beats in 4/4.",
    [
      "V:1 clef=treble",
      "\"1 I(C)\" [CEG]2 [CEG]2 | [CEG]2 [CEG]2 |",
      "\"5 IV(F)\" [FAC]2 [FAC]2 | \"8 I(C)\" [CEG]2 [CEG]2 |",
      "\"9 V(G)\" [GBd]2 \"10 IV(F)\" [FAC]2 | \"12 I(C)\" [CEG]2 [CEG]2 |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/2", key: "C" }
  ),
  "styles-classical-01": portrait(
    "Show a four‑voice staff excerpt of a Haydn‑style string quartet phrase, highlighting the Alberti‑bass pattern in the accompaniment, a question‑answer melodic contour, and classic dynamic markings (p – f). Include a caption that labels each voice (first violin, second violin, viola, cello) to illustrate balanced homophonic texture.",
    WIKI + "/1/1e/Wolfgang-amadeus-mozart_1.jpg"
  ),
  "styles-folk-dances-01": portrait(
    "Irish stepdance dress detail — representative image for reel, jig, waltz, and hornpipe dance styles.",
    IMAGES.irishDancing
  ),
  "styles-jazz-01": portrait(
    "Duke Ellington — representative image for jazz combo colour, swing, and arrangement.",
    IMAGES.ellington
  ),
  "styles-modern-01": portrait(
    "Moog modular synthesizer — representative image for modern/trad crossover production tools.",
    IMAGES.moog
  ),
  "styles-modes-01": portrait(
    "Guillaume Dufay manuscript portrait — representative image for modal scales and early polyphony.",
    IMAGES.dufay
  ),
  "styles-pop-01": portrait(
    "Turntable top view — representative image for pop song form and recorded-music production.",
    IMAGES.turntable
  ),
  "styles-romantic-01": portrait(
    "Create a stylized portrait of a 19th‑century Romantic composer (e.g., Chopin) surrounded by visual symbols of expanded harmony such as an augmented‑sixth chord, a borrowed chord, and a flowing, lyrical melodic line.",
    WIKI + "/e/e8/Frederic_Chopin_photo.jpeg"
  ),
  "transpose-capo-01": noExample(),
  "transpose-chords-01": notation(
    "I–V–vi–IV in C major and transposed up a whole step to D major: stacked block chords with Roman numerals; each chord lasts two beats.",
    [
      "V:1 clef=treble",
      "[K:C] \"I(C)\" [CEG]2 | \"V(G)\" [GBd]2 | \"vi(Am)\" [Ace]2 | \"IV(F)\" [FAC]2 |",
      "[K:D] \"I(D)\" [DFA]2 | \"V(A)\" [Ace]2 | \"vi(Bm)\" [Bdf]2 | \"IV(G)\" [GBd]2 |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/2", key: "C" }
  ),
  "transpose-melody-01": notation(
    "Display a four‑note phrase (D F♯ A B) and its transposition up a major second (E G♯ B C♯) on a single staff, highlighting the interval shift and key‑signature change.",
    [
      "V:1 clef=treble",
      "[K:D] D ^F A B | [K:E] E ^G B ^c |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "D" }
  ),
  "transpose-modes-01": notation(
    "Show the D Dorian and G Mixolydian scales on a treble‑clef staff using ABC notation, ensuring all notes fit within the staff with no ledger lines.",
    [
      "V:1 clef=treble",
      "[D Dorian] D E F G | A B c d |",
      "[G Mixolydian] G A B c | d e f g ||",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/8", key: "C" }
  ),
  "transpose-why-01": notation(
    "Show a short four‑note melody (C D E F) and its transposition up a whole step (D E F♯ G) on the staff, highlighting the unchanged interval pattern.",
    [
      "V:1 clef=treble",
      "[K:C] C D E F | [K:D] D E ^F G |",
    ].join('\n'),
    { meter: "4/4", noteLength: "1/4", key: "C" }
  ),
}

Object.assign(THEORY_LESSON_EXAMPLES, celticExamples)

export default THEORY_LESSON_EXAMPLES
